import {
  createContext,
  lazy,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type ReactNode,
} from "react";

import type { Session } from "@/lib/types";

import { FronteiraDaCarga } from "./FronteiraDaCarga";
import type { IdDeAcaoRapida } from "./itens-da-paleta";

// TUT-496 — a paleta Ctrl+K do escritório: o estado de aberta/fechada, o atalho
// e a porta que a busca da barra de cima usa para abri-la.
//
// Nada daqui puxa o cmdk nem os formulários: a paleta e os diálogos de criação
// são carregados SOB DEMANDA (o primeiro Ctrl+K, a primeira criação), então o
// chunk de entrada não cresce. E nada daqui consulta o servidor: a paleta só
// monta, e só então pede dados, depois que alguém a abre.

/**
 * O que a paleta recebe do provider. TUT-546 — exportado: a paleta do PORTAL
 * (`pages/portal/PaletaDoPortal.tsx`) é outra peça com as mesmas props, e o
 * provider a carrega por `carregarPaleta`.
 */
export type PropsDaPaleta = {
  session: Session;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  termoInicial: string;
  aoEscolherAcao: (id: IdDeAcaoRapida) => void;
  aoFecharDeVez: () => void;
};
type PropsDasAcoes = { acao: IdDeAcaoRapida; onClose: () => void };

/**
 * Um `lazy` que pode ser refeito. O `React.lazy` guarda a promessa REJEITADA
 * para sempre: sem trocar o componente, "Tentar de novo" repetiria o mesmo erro.
 */
function useCarregavel<P extends object>(
  importar: () => Promise<{ default: ComponentType<P> }>,
) {
  const [tentativa, setTentativa] = useState(0);
  const importador = useRef(importar);
  // `tentativa` é a identidade: cada tentativa cria o `lazy` de novo.
  const Componente = useMemo(
    () => lazy(() => importador.current()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tentativa],
  );
  return { Componente, tentativa, tentar: () => setTentativa((n) => n + 1) };
}

interface ContextoDaPaleta {
  /**
   * Abre a paleta. `texto` é o que a pessoa já digitou ou colou na busca do
   * topo; chamadas seguidas ANTES de a paleta montar se somam (quem digita
   * "silva" rápido na primeira vez, com o chunk ainda baixando, não perde letra).
   */
  abrir: (texto?: string) => void;
}

const Contexto = createContext<ContextoDaPaleta | null>(null);

export function useAbrirPaleta(): ContextoDaPaleta["abrir"] {
  const contexto = useContext(Contexto);
  if (!contexto) throw new Error("useAbrirPaleta fora do PaletaProvider");
  return contexto.abrir;
}

/**
 * Há OUTRO modal aberto? Então Ctrl+K não empilha a paleta sobre ele. Só conta
 * o modal de verdade (o Dialog, o AlertDialog e a Sheet da casa): o conteúdo do
 * Radix com `role` de diálogo, aberto, FORA de um popper. O Popover (o sino, o
 * menu da conta, os filtros) também tem `role` de diálogo, mas mora num
 * `data-radix-popper-content-wrapper`, não prende o foco nem esconde a página, e
 * não bloqueia nada. É uma CONSULTA ao que o Radix pôs no documento, e não um
 * `role` escrito à mão num componente — o seletor vai sem aspas de propósito,
 * para a trava `modal-dialog-pattern.test.ts` seguir lendo só o que de fato
 * desenha um diálogo.
 */
export function haOutroModalAberto(): boolean {
  const candidatos = document.querySelectorAll(
    "[role=dialog][data-state=open], [role=alertdialog][data-state=open], [role=dialog][aria-modal=true]",
  );
  return Array.from(candidatos).some((no) => !no.closest("[data-radix-popper-content-wrapper]"));
}

const carregarPaletaDoEscritorio = () => import("./PaletaDeComandos");

export function PaletaProvider({
  session,
  children,
  carregarPaleta = carregarPaletaDoEscritorio,
}: {
  session: Session;
  children: ReactNode;
  /**
   * TUT-546 — QUAL paleta o atalho abre. O padrão é a do escritório; o portal
   * passa a dele, que lê as seções e os itens do portal e nunca alcança o
   * menu nem os diálogos do escritório. O atalho, a busca do topo e a carga
   * sob demanda são os mesmos.
   */
  carregarPaleta?: () => Promise<{ default: ComponentType<PropsDaPaleta> }>;
}) {
  const [aberta, setAberta] = useState(false);
  const abertaRef = useRef(false);
  // A paleta é montada na primeira abertura e fica: a saída animada precisa
  // dela montada, e reabrir não baixa nada de novo.
  const [jaAbriu, setJaAbriu] = useState(false);
  const [termoInicial, setTermoInicial] = useState("");
  const [acao, setAcao] = useState<IdDeAcaoRapida | null>(null);
  const acaoPendente = useRef<IdDeAcaoRapida | null>(null);
  const paleta = useCarregavel<PropsDaPaleta>(carregarPaleta);
  const acoes = useCarregavel<PropsDasAcoes>(() => import("./AcoesRapidas"));

  const mudarAberta = useCallback((valor: boolean) => {
    abertaRef.current = valor;
    setAberta(valor);
  }, []);

  const abrir = useCallback(
    (texto = "") => {
      // Um popover aberto (o sino, o menu da conta) se fecha sozinho: o Radix o
      // dispensa quando o foco vai para a paleta, que é modal. Medido na e2e
      // (sabotada sem nenhum fechamento explícito, o menu fechou igual); um Esc
      // despachado à mão aqui foi tentado e descartado por não mudar nada.
      setTermoInicial((atual) => (abertaRef.current ? atual + texto : texto));
      setJaAbriu(true);
      mudarAberta(true);
    },
    [mudarAberta],
  );

  useEffect(() => {
    function aoTeclar(event: KeyboardEvent) {
      if (event.key.toLowerCase() !== "k") return;
      if (!(event.ctrlKey || event.metaKey) || event.altKey || event.shiftKey) return;
      if (event.isComposing) return;
      if (aberta) {
        event.preventDefault();
        mudarAberta(false);
        return;
      }
      // Com outro modal aberto a combinação não é nossa: o navegador a recebe
      // como sempre (a barra de endereço), e nada é engolido.
      if (haOutroModalAberto()) return;
      // O navegador usa Ctrl+K para a barra de endereço e a busca dele; dentro
      // da página o gesto é nosso, também com o foco num campo de texto.
      event.preventDefault();
      abrir();
    }
    window.addEventListener("keydown", aoTeclar);
    return () => window.removeEventListener("keydown", aoTeclar);
  }, [aberta, abrir, mudarAberta]);

  const valor = useMemo(() => ({ abrir }), [abrir]);

  return (
    <Contexto.Provider value={valor}>
      {children}
      {jaAbriu ? (
        <FronteiraDaCarga
          key={paleta.tentativa}
          mensagem="Não foi possível abrir a busca."
          aoTentar={paleta.tentar}
          aoFechar={() => {
            mudarAberta(false);
            setJaAbriu(false);
          }}
        >
          <Suspense fallback={null}>
            <paleta.Componente
              session={session}
              open={aberta}
              onOpenChange={mudarAberta}
              termoInicial={termoInicial}
              aoEscolherAcao={(id) => {
                acaoPendente.current = id;
              }}
              // O diálogo da ação sobe depois que o foco voltou a quem abriu a
              // paleta: assim o fechamento dele devolve o foco ao mesmo lugar, e
              // os dois modais nunca disputam o foco ao mesmo tempo.
              aoFecharDeVez={() => {
                const id = acaoPendente.current;
                acaoPendente.current = null;
                if (id) setTimeout(() => setAcao(id), 0);
              }}
            />
          </Suspense>
        </FronteiraDaCarga>
      ) : null}
      {acao ? (
        <FronteiraDaCarga
          key={acoes.tentativa}
          mensagem="Não foi possível abrir o formulário."
          aoTentar={acoes.tentar}
          aoFechar={() => setAcao(null)}
        >
          <Suspense fallback={null}>
            <acoes.Componente acao={acao} onClose={() => setAcao(null)} />
          </Suspense>
        </FronteiraDaCarga>
      ) : null}
    </Contexto.Provider>
  );
}
